// Import style
import './style.scss';

// Import utilities
const {
	abbreviateText,
	isDefined,
	isEmpty
} = _uo.utility;

import Tokens from '@builder-legacy/tokens';

/**
 * Parses a dynamic sentence to add real data
 * Algorithm:
 *
 * Given a string with curly brackets (hereafter, the dynamic pill), like:
 * "The user passed {{a quiz:QUIZ}}"
 *
 * 1. Look for all the dynamic pill using a regular expression. Iterate them and replace
 *    them with <span>. Add data-field with the field ID, and use the default text of
 *    the dynamic pill as innerText.
 * 2. Iterate the fields (paramater) and replace the dynamic pills with the values.
 * 3. Add wrapper around text nodes (plain parts)
 *
 * @param {Object} options                 The options
 * @param {String} options.dynamicSentence The dynamic sentence
 * @param {Object} options.fields          The fields
 * @param {Object} options.itemID          The ID of the action where we're parsing these tokens
 * @return {Node}                          The node with the dynamic sentence
 */
const parseDynamicSentence = ( { dynamicSentence = '', fields = {}, itemID, abbreviateFields = false } ) => {
	// Create wrapper
	const $wrapper = document.createElement( 'span' );
	$wrapper.classList.add( 'uap-dynamic-sentence' );
	$wrapper.setAttribute( 'dir', 'auto' );

	// Regex to get the dynamic pills
	const pillsRegex = new RegExp( /\{\{(.*?)(?:\:(.*?)(?:\:(.*?))?)?\}\}/gm );

	// Create instance of Tokens. We will use it to parse tokens in the dynamic sentence
	const tokens = new Tokens( 
		null, 
		'text', 
		{
			itemID: itemID
		}
	);

	/**
	 * 1. Look for all the dynamic pills using a regular expression. Iterate them and
	 * replace them with <span>s. Add data-field with the field ID, and use the default
	 * text of the dynamic pill as innerText.
	 *
	 * @param {String} match          The full match. Ex: {{a quiz:QUIZ:ALL_FIELDS}}
	 * @param {String} defaultText    The default text, used if there isn't a field. Ex: "a quiz"
	 * @param {String} fieldId        The field ID. Ex: QUIZ
	 * @param {String} [fieldGroupId] The field group ID. OPTIONAL. Ex: ALL_FIELDS
	 * @return {String}                The <span> with the dynamic pill data
	 */
	dynamicSentence = dynamicSentence.replaceAll( pillsRegex, ( match, defaultText, fieldId, fieldGroupId ) => {
		// Create dynamic pill
		// Even though we will return a string, it helps to the readability of the code
		const $dynamicPill = document.createElement( 'span' );

		// Add "filled class"
		$dynamicPill.classList.add( 'uap-dynamic-sentence-pill' );

		// Add the default text as content
		$dynamicPill.innerText = defaultText;

		// Check if has a field ID
		if ( ! isEmpty( fieldId ) ) {
			// Add the field ID
			$dynamicPill.setAttribute( 'data-field', fieldId );
		}

		// Check if it has a field group ID
		if ( ! isEmpty( fieldGroupId ) ) {
			// Add the field group ID
			$dynamicPill.setAttribute( 'data-field-group', fieldGroupId );
		}

		// Return the Node as string
		return $dynamicPill.outerHTML;
	} );

	// Add it to the wrapper
	$wrapper.innerHTML = dynamicSentence;

	/**
	 * 2. Iterate the fields (paramater) and replace the dynamic pills with the values.
	 *
	 * @param {String}      fieldId    The field ID
	 * @param {String|Node} fieldValue The field value
	 * @return {undefined}
	 */
	Object.entries( fields ).forEach( ( [ fieldId, fieldValue ] ) => {
		// Find the dynamic pill using the field ID
		const $dynamicPill = $wrapper.querySelector( `span[data-field="${ fieldId }"]` );

		// Check if the pill exists
		if ( ! isDefined( $dynamicPill ) ) {
			return;
		}

		// Check if there is a "readable" field value available
		// This is used for selects. The field value is the "value", while the
		// readable value is the option "text"
		if ( isDefined( fields[ `${ fieldId }_readable` ] ) ) {
			// Override the field value
			fieldValue = fields[ `${ fieldId }_readable` ];
		}

		// Empty content
		$dynamicPill.innerText = '';

		// Check if we have to add the label
		if ( isDefined( fields[ `${ fieldId }_label` ] ) ) {
			$dynamicPill.insertAdjacentHTML( 'afterBegin', `<span class="uap-dynamic-sentence-pill-label">${ fields[ `${ fieldId }_label` ] }: </span>` );
		}

		// Add "filled class"
		$dynamicPill.classList.add( 'uap-dynamic-sentence-pill--filled' );

		// Replace the content with the field value
		// First, try to add it as a Node
		try {
			// Add element
			$dynamicPill.appendChild( fieldValue );
		} catch ( e ) {
			// Verify that the field value is not empty
			if ( ! isEmpty( fieldValue ) ) {
				if ( abbreviateFields ) {
					fieldValue = abbreviateText( fieldValue );
				}
				
				// If it fails, it's probably because this is a string
				// Add it as string, but try to parse the tokens (if there is any)
				try {
					// Parse tokens. This will return a Node
					const $stringWithTokens = tokens.renderTextWithTokens( fieldValue, {
						itemID: itemID
					} );

					// Append string with tokens
					$dynamicPill.appendChild( $stringWithTokens );
				} catch ( ex ) {
					// Well, that also failed :) Let's just add it as a plain string
					$dynamicPill.insertAdjacentHTML( 'beforeEnd', fieldValue );
				}
			} else {
				// Add text to let the user know the string was added, but it's just empty
				$dynamicPill.insertAdjacentHTML( 'beforeEnd', `<em class="uap-empty-value">${ UncannyAutomator._core.i18n.emptyValue }</em>` );
			}
		}
	} );

	/**
	 * 3. Add wrapper around plain text
	 */
	$wrapper.childNodes.forEach( ( $sentencePart ) => {
		// Check if it's a text node
		if ( $sentencePart.nodeType !== Node.TEXT_NODE ) {
			return;
		}

		// Create wrapper
		const $plainWrapper = document.createElement( 'span' );
		$plainWrapper.classList.add( 'uap-dynamic-sentence-plain' );

		// Insert plainWrapper before the text node
		$wrapper.insertBefore( $plainWrapper, $sentencePart );

		// Wrap the text node
		$plainWrapper.appendChild( $sentencePart );
	} );

	return $wrapper;
};

export default parseDynamicSentence;
